import { openAuthPopup } from '@netlify/app-auth'
import { colorFacetsNeutralLight200 } from '@netlify/netlify-design-tokens'
import { Pane } from 'evergreen-ui'
import dynamic from 'next/dynamic'

import HudLoading from '~/components/HudLoading'
import { SESSION_STORAGE_KEY } from '~/helpers/auth'
import { useStore } from '~/store/hooks'
import { BASE_PATH } from '~/utils/tokens'

// Note: options need to be passed literally for the loading component to appear
// See https://github.com/vercel/next.js/issues/31520
const AuthPane = dynamic(() => import('~/components/AuthPane'), {
  loading: () => <HudLoading />,
  ssr: false,
})
const HudContainer = dynamic(() => import('~/components/HudContainer'), {
  loading: () => <HudLoading />,
  ssr: false,
})
const HudError = dynamic(() => import('~/components/HudError'), {
  loading: () => <HudLoading />,
  ssr: false,
})
const JoinTeamAsReviewer = dynamic(() => import('~/components/JoinTeamAsReviewer'), {
  loading: () => <HudLoading />,
  ssr: false,
})

const SHEET_WIDTH = 768

export default function HudSheet() {
  const access = useStore(state => state.ref.access)
  const deploy = useStore(state => state.netlify.deploy)
  const merge = useStore(state => state.ref.merge)

  const sideSheetBody = {
    UNAUTHENTICATED: <AuthPane />,
    LOADING: <HudLoading />,
    UNAUTHORIZED: <JoinTeamAsReviewer />,
    PENDING_APPROVAL: <HudContainer />,
    APPROVED: <HudContainer />,
    NEED_GITLAB_TOKEN: (
      <HudError
        buttonCTA="Connect to GitLab"
        description="An access token from GitLab is required to enable collaborative features on your Deploy Previews."
        docs="https://docs.netlify.com/site-deploys/deploy-previews/#netlify-drawer-is-missing-for-a-site-connected-to-gitlab"
        docsCTA="Learn more about GitLab tokens"
        heading="Netlify needs your GitLab token"
        img="/img/integrations/gitlab-mark.svg"
        onClick={() => {
          const popupUrl =
            merge.vcs === 'gitlab_self_hosted'
              ? `${BASE_PATH}/auth/token?service=${merge.vcs}&host=${deploy.vcsHost}`
              : `${BASE_PATH}/auth/oauth?service=${merge.vcs}`
          openAuthPopup({
            url: popupUrl,
            popupWidth: 540,
            popupHeight: 600,
            storageKeyOverride: SESSION_STORAGE_KEY,
          })
        }}
      />
    ),
    NEED_BITBUCKET_TOKEN: (
      <HudError
        buttonCTA="Connect to Bitbucket"
        description="An access token from Bitbucket is required to enable collaborative features on your Deploy Previews."
        heading="Netlify needs your Bitbucket token"
        img="/img/integrations/bitbucket-mark.svg"
        onClick={() => {
          const popupUrl =
            merge.vcs === 'bitbucket_self_hosted'
              ? `${BASE_PATH}/auth/token?service=${merge.vcs}&host=${deploy.vcsHost}`
              : `${BASE_PATH}/auth/oauth?service=${merge.vcs}`
          openAuthPopup({
            url: popupUrl,
            popupWidth: 540,
            popupHeight: 600,
            storageKeyOverride: SESSION_STORAGE_KEY,
          })
        }}
      />
    ),
    NEED_ADO_TOKEN: (
      <HudError
        buttonCTA="Connect to Azure DevOps"
        description="An access token from Azure DevOps is required to enable collaborative features on your Deploy Previews."
        // docs="https://docs.netlify.com/site-deploys/deploy-previews/#netlify-drawer"
        heading="Netlify needs your Azure DevOps token."
        img="/img/integrations/azure-devops-mark.svg"
        onClick={() => {
          openAuthPopup({
            url: `${BASE_PATH}/auth/oauth?service=${merge.vcs}`,
            popupWidth: 540,
            popupHeight: 600,
            storageKeyOverride: SESSION_STORAGE_KEY,
          })
        }}
      />
    ),
    NEED_APP: (
      <HudError
        buttonCTA="Get the app"
        description="The Netlify GitHub App is required to enable collaborative features on your Deploy Previews."
        docs="https://docs.netlify.com/site-deploys/deploy-previews/#netlify-drawer"
        docsCTA="Learn more about the Netlify GitHub App"
        heading="Netlify GitHub App not installed"
        href="https://github.com/apps/netlify/installations/new"
        img="/img/logo/netlify-github-app.png"
      />
    ),
    ERRORED: (
      <HudError
        buttonCTA="Visit Netlify Support Forums"
        description="Please refresh and try again. If the issue persists, please let us know in our support forums."
        heading="Something went wrong"
        href="https://answers.netlify.com"
        img="/img/icons/warning-icon.svg"
      />
    ),
  }

  return (
    <Pane aria-label="Netlify Drawer" className="hud" height="100vh" position="fixed" width={SHEET_WIDTH}>
      <Pane
        background="white"
        borderBottomLeftRadius={8}
        borderBottomRightRadius={8}
        borderTopColor={colorFacetsNeutralLight200}
        borderTopStyle="solid"
        borderTopWidth={1}
        boxShadow="0 6px 6px rgba(0, 0, 0, 0.2)"
        overflow="auto"
      >
        {sideSheetBody[access]}
      </Pane>
    </Pane>
  )
}
